<script setup>
import {ref, onMounted, computed, watch, onUnmounted} from 'vue'
import {useRouter, useRoute} from 'vue-router'
import axios from 'axios'
import { createExamClock } from '@/composables/examClock'

const router = useRouter()
const route = useRoute()

const exams = ref([])
const loading = ref(true)

const user = JSON.parse(localStorage.getItem('user') || '{}')
const academicYearId = user.academic_year_id || null

// Server-authoritative monotonic clock copied from Tarekmath. A server instant is
// pinned to performance.now(), so device wall-clock and timezone changes do not
// change exam availability after synchronization.
const synchronizedClock = createExamClock()
const nowTick = ref(0)
const perfNow = () => performance.now()
const serverNow = () => { void nowTick.value; return synchronizedClock.now() }

/* ─── Computed ─── */
const durationRequired = computed(() => route.query.type === 'online' ? 1 : 0)
const isOnline = computed(() => route.query.type === 'online')
const pageTitle = computed(() => isOnline.value ? 'الامتحانات الأونلاين' : 'الواجبات المنزلية')
const pageIcon  = computed(() => isOnline.value ? '📝' : '🏠')

/* ─── Helpers ─── */
const examStartMs = (exam) => (exam.exam_start_at ? new Date(exam.exam_start_at).getTime() : null)
const examEndMs = (exam) => (exam.effective_exam_end_at || exam.exam_end_at) ? Date.parse(exam.effective_exam_end_at || exam.exam_end_at) : null
const clockSynced = () => synchronizedClock.now() !== null

const isExamEnded = (exam) => {
  const end = examEndMs(exam)
  if (end == null) return false
  if (!clockSynced()) return exam.exam_status === 'expired'
  return serverNow() >= end
}
const isExamStarted = (exam) => {
  const start = examStartMs(exam)
  if (start == null) return true
  if (!clockSynced()) return exam.exam_status !== 'not_started'
  return serverNow() >= start
}
const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('ar-EG', { year:'numeric', month:'long', day:'numeric' })

const examStatus = (exam) => {
  if (isExamEnded(exam)) return { label: 'انتهى', cls: 'ended' }
  if (exam.taken && isOnline.value) return { label: 'تم التأدية', cls: 'taken' }
  if (!isExamStarted(exam)) return { label: 'لم يبدأ بعد', cls: 'pending' }
  return { label: 'متاح الآن', cls: 'live' }
}

const canStart = (exam) =>
    isExamStarted(exam) && !isExamEnded(exam) &&
    (!exam.taken || !isOnline.value)

/* ─── Server-time synchronization ─── */
let syncSeq = 0
let lastSyncAt = 0
const SYNC_THROTTLE_MS = 1500

const applyServerNow = (iso) => synchronizedClock.sync(iso)

const syncServerTime = async ({ force = false, showLoading = false } = {}) => {
  const nowM = perfNow()
  if (!force && (nowM - lastSyncAt) < SYNC_THROTTLE_MS) return
  lastSyncAt = nowM
  const seq = ++syncSeq
  if (showLoading) loading.value = true
  try {
    const response = await axios.get('/exams_web', {
      params: { academic_year_id: academicYearId, duration_required: durationRequired.value }
    })
    if (seq !== syncSeq) return
    exams.value = response.data.data || []
    applyServerNow(response.data.server_now)
  } catch (e) {
    if (seq !== syncSeq) return
    console.error(e)
    if (showLoading) exams.value = []
  } finally {
    if (showLoading) loading.value = false
  }
}

/* ─── Actions ─── */
const goToExam = (exam) => {
  if (!canStart(exam)) return
  router.push(`/exam/${exam.id}?type=${exam.duration_required === 1 ? 'online' : 'homework'}`)
}

/* ─── Lifecycle ─── */
const onVisibility = () => { if (document.visibilityState === 'visible') syncServerTime() }
const onFocus = () => syncServerTime()
const onPageShow = () => syncServerTime()

let tickInterval = null
let unwatchType = null
onMounted(() => {
  syncServerTime({ force: true, showLoading: true })
  tickInterval = setInterval(() => { nowTick.value++ }, 1000)
  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('focus', onFocus)
  window.addEventListener('pageshow', onPageShow)
  unwatchType = watch(() => route.query.type, () =>
    syncServerTime({ force: true, showLoading: true })
  )
})
onUnmounted(() => {
  if (tickInterval) clearInterval(tickInterval)
  if (unwatchType) unwatchType()
  document.removeEventListener('visibilitychange', onVisibility)
  window.removeEventListener('focus', onFocus)
  window.removeEventListener('pageshow', onPageShow)
})
</script>

<template>
  <div class="exams-page">
    <div class="container">

      <!-- Page header -->
      <div class="exams-header">
        <div class="exams-header-icon">{{ pageIcon }}</div>
        <h1 class="exams-header-title">{{ pageTitle }}</h1>
        <p class="exams-header-sub">
          {{ isOnline ? 'اختر الامتحان الذي تريد البدء به' : 'الواجبات المطلوبة منك هذه الفترة' }}
        </p>
      </div>

      <!-- Loading skeleton -->
      <div v-if="loading" class="exams-grid">
        <div v-for="n in 6" :key="n" class="exam-skeleton">
          <div class="skel skel-head"></div>
          <div class="skel skel-line"></div>
          <div class="skel skel-line skel-short"></div>
          <div class="skel skel-btn"></div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else-if="!exams.length" class="exams-empty">
        <div class="empty-icon">{{ isOnline ? '📋' : '📚' }}</div>
        <h2>لا توجد عناصر حالياً</h2>
        <p>سيتم إضافة {{ isOnline ? 'امتحانات' : 'واجبات' }} قريباً، راجع لاحقاً</p>
      </div>

      <!-- Exam cards -->
      <div v-else class="exams-grid">
        <div
          v-for="exam in exams"
          :key="exam.id"
          class="exam-card"
          :class="[
            { 'exam-card--disabled': !canStart(exam) },
            `exam-card--${examStatus(exam).cls}`
          ]"
          @click="goToExam(exam)"
        >
          <!-- Card header with gradient -->
          <div class="exam-card-header" :class="isOnline ? 'grad-primary' : 'grad-accent'">
            <div class="exam-card-type-badge">
              {{ isOnline ? 'امتحان' : 'واجب' }}
            </div>
            <h3 class="exam-card-title">{{ exam.title }}</h3>
            <p class="exam-card-lesson">{{ exam.lesson?.title || 'بدون درس' }}</p>
          </div>

          <!-- Status badge -->
          <div class="exam-status-wrap">
            <span class="exam-status" :class="`exam-status--${examStatus(exam).cls}`">
              <span class="exam-status-dot"></span>
              {{ examStatus(exam).label }}
            </span>
          </div>

          <!-- Card body -->
          <div class="exam-card-body">
            <div class="exam-info-list">
              <div class="exam-info-item" v-if="exam.personal_duration_minutes">
                <span class="exam-info-icon">⏱</span>
                <span>مدة شخصية: {{ exam.personal_duration_minutes }} دقيقة، بحد أقصى نهاية الوقت العام</span>
              </div>
              <div class="exam-info-item">
                <span class="exam-info-icon">📅</span>
                <span>{{ formatDate(exam.date) }}</span>
              </div>
              <div class="exam-info-item" v-if="exam.from_time">
                <span class="exam-info-icon">🕒</span>
                <span>{{ exam.from_time }} — {{ exam.to_time }}</span>
              </div>
              <div class="exam-info-item">
                <span class="exam-info-icon">🎯</span>
                <span>الدرجة الكلية: <strong>{{ exam.total_degree }}</strong></span>
              </div>
              <div class="exam-info-item">
                <span class="exam-info-icon">❓</span>
                <span>{{ exam.questions?.length || 0 }} سؤال</span>
              </div>
            </div>

            <!-- Start button -->
            <button
              v-if="canStart(exam)"
              class="exam-start-btn"
              :class="isOnline ? 'btn-primary' : 'btn-accent'"
              @click.stop="goToExam(exam)"
            >
              {{ isOnline ? '✍️ ابدأ الامتحان' : '📖 عرض الواجب' }}
            </button>

            <!-- Ended notice -->
            <div v-else-if="isExamEnded(exam)" class="exam-notice exam-notice--red">
              ⏰ انتهى وقت {{ isOnline ? 'الامتحان' : 'الواجب' }}
            </div>
            <div v-else-if="exam.taken && isOnline" class="exam-notice exam-notice--gray">
              ✅ لقد أكملت هذا الامتحان
            </div>
          </div>

        </div>
      </div>

    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&display=swap');

.exams-page {
  padding: 2rem 0 4rem;
  min-height: calc(100vh - 70px);
  font-family: 'Cairo', sans-serif;
  direction: rtl;
  background: #0D0D0D;
}

.container { max-width: 1200px; margin: 0 auto; padding: 0 1.25rem; }

/* ─── Header ─── */
.exams-header {
  text-align: center; padding: 3rem 1rem 2.5rem;
}
.exams-header-icon { font-size: 3rem; margin-bottom: .75rem; }
.exams-header-title {
  font-size: 2.2rem; font-weight: 800; margin-bottom: .5rem;
  background: linear-gradient(135deg,#D4AF37,#C89B2D);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
}
.exams-header-sub { color: rgba(240,237,228,0.6); font-size: 1rem; }

/* ─── Grid ─── */
.exams-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
}

/* ─── Skeleton ─── */
.exam-skeleton {
  background: #fff; border-radius: 20px; overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,.06);
}
.skel {
  background: linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite; border-radius: 8px;
}
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
.skel-head { height: 110px; border-radius: 0; }
.skel-line { height: 14px; margin: 1rem 1.25rem .5rem; }
.skel-short { width: 60%; }
.skel-btn { height: 44px; margin: .75rem 1.25rem 1.5rem; border-radius: 12px; }

/* ─── Empty ─── */
.exams-empty {
  text-align: center; padding: 5rem 1rem;
  color: #6B7280;
}
.empty-icon { font-size: 4rem; margin-bottom: 1rem; }
.exams-empty h2 { font-size: 1.5rem; font-weight: 700; color: #1A1A1A; margin-bottom: .5rem; }
.exams-empty p { font-size: .95rem; }

/* ─── Card ─── */
.exam-card {
  background: #fff; border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0,0,0,.07);
  cursor: pointer; transition: all .35s cubic-bezier(.4,0,.2,1);
  border: 2px solid transparent; overflow: hidden;
  position: relative;
}
.exam-card:hover:not(.exam-card--disabled) {
  transform: translateY(-8px);
  box-shadow: 0 20px 50px rgba(212,175,55,.15);
  border-color: rgba(212,175,55,.2);
}
.exam-card--disabled { opacity: .55; cursor: not-allowed; }
.exam-card--disabled:hover { transform: none; box-shadow: 0 4px 16px rgba(0,0,0,.07); border-color: transparent; }

/* Card header */
.exam-card-header {
  padding: 1.5rem 1.25rem 1.25rem;
  color: #fff; position: relative;
}
.grad-primary { background: linear-gradient(135deg,#D4AF37 0%,#C89B2D 100%); }
.grad-accent  { background: linear-gradient(135deg,#D4AF37 0%,#B8860B 100%); }

.exam-card-type-badge {
  display: inline-block; background: rgba(255,255,255,.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,.3);
  border-radius: 99px; padding: .2rem .75rem;
  font-size: .75rem; font-weight: 700; margin-bottom: .75rem;
}
.exam-card-title { font-size: 1.2rem; font-weight: 800; margin-bottom: .35rem; line-height: 1.3; }
.exam-card-lesson { font-size: .85rem; opacity: .88; margin: 0; }

/* Status */
.exam-status-wrap { padding: .75rem 1.25rem 0; }
.exam-status {
  display: inline-flex; align-items: center; gap: .4rem;
  font-size: .8rem; font-weight: 700; padding: .3rem .9rem;
  border-radius: 99px;
}
.exam-status-dot { width: 7px; height: 7px; border-radius: 50%; animation: pulse 1.5s infinite; flex-shrink: 0; }
.exam-status--live   { background: rgba(34,197,94,.12);  color: #16a34a; }
.exam-status--live .exam-status-dot { background: #22C55E; }
.exam-status--ended  { background: rgba(239,68,68,.1);   color: #dc2626; }
.exam-status--ended .exam-status-dot { background: #EF4444; animation: none; }
.exam-status--taken  { background: rgba(212,175,55,.1);   color: #D4AF37; }
.exam-status--taken .exam-status-dot { background: #D4AF37; animation: none; }
.exam-status--pending{ background: rgba(245,158,11,.1);  color: #b45309; }
.exam-status--pending .exam-status-dot { background: #F59E0B; }
@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.3} }

/* Card body */
.exam-card-body { padding: 1rem 1.25rem 1.5rem; }
.exam-info-list { display: flex; flex-direction: column; gap: .5rem; margin-bottom: 1.25rem; }
.exam-info-item { display: flex; align-items: center; gap: .6rem; color: #475569; font-size: .9rem; }
.exam-info-icon { font-size: 1rem; flex-shrink: 0; }

/* Buttons */
.exam-start-btn {
  width: 100%; padding: .85rem;
  border: none; border-radius: 14px;
  font-size: 1rem; font-weight: 700;
  cursor: pointer; transition: all .3s;
  font-family: 'Cairo', sans-serif;
}
.btn-primary { background: linear-gradient(135deg,#D4AF37,#C89B2D); color: #fff; box-shadow: 0 8px 20px rgba(212,175,55,.25); }
.btn-primary:hover { box-shadow: 0 14px 30px rgba(212,175,55,.4); transform: translateY(-2px); }
.btn-accent  { background: linear-gradient(135deg,#D4AF37,#B8860B); color: #fff; box-shadow: 0 8px 20px rgba(212,175,55,.2); }
.btn-accent:hover  { box-shadow: 0 14px 30px rgba(212,175,55,.35); transform: translateY(-2px); }

/* Notices */
.exam-notice {
  text-align: center; padding: .75rem; border-radius: 12px;
  font-weight: 700; font-size: .9rem;
}
.exam-notice--red  { background: rgba(239,68,68,.08);  color: #dc2626; }
.exam-notice--gray { background: rgba(212,175,55,.07);  color: #D4AF37; }

@media(max-width:600px) {
  .exams-header-title { font-size: 1.7rem; }
  .exams-grid { grid-template-columns: 1fr; }
}
</style>
